import { Component, effect, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventType } from '@azure/msal-browser';
import { environment } from '@environments';
import posthog from 'posthog-js';

import { filter, Subject, takeUntil } from 'rxjs';
import { CommonModule, ViewportScroller } from '@angular/common';
import { AuthService } from './shared/services/auth/auth.service';
import { LogsPageStateService } from '@logs';
import { StoltApiService } from '@services';

type ProfileType = {
  givenName?: string;
  surname?: string;
  displayName?: string;
  userPrincipalName?: string;
  mail?: string;
  id?: string;
};

@Component({
  selector: 'app-root',
  imports: [RouterModule, CommonModule],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  title = 'ST-A-Eye';
  loginDisplay = false;
  isLoading = true;
  private readonly _destroying$ = new Subject<void>();
  private identifiedPosthogUserId?: string;
  private pendingPosthogLoginCapture = false;
  private readonly logsPageState = inject(LogsPageStateService);

  constructor(
    private readonly broadcastService: MsalBroadcastService,
    private readonly msalService: MsalService,
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly viewportScroller: ViewportScroller,
    private readonly authService: AuthService,
    private readonly apiService: StoltApiService,
  ) {
    this.router.events.subscribe((event: unknown) => {
      if (event instanceof NavigationEnd) {
        if (!this.logsPageState.isScrollRestorePending()) {
          this.viewportScroller.scrollToPosition([0, 0]);
        }
        if (this.msalService.instance.getAllAccounts().length > 0) {
          this.checkAndSetActiveAccount();
        }
        // SPA route changes: capture pageviews manually (capture_pageview is false in init).
        if (environment.POSTHOG_ENABLED_TRACKING) {
          posthog.capture('$pageview');
        }
      }
    });

    effect(() => {
      const user = this.authService.UserObj().user as ProfileType | undefined;
      if (!environment.POSTHOG_ENABLED_TRACKING || !user?.givenName) {
        return;
      }

      const distinctId = user.id;
      if (!distinctId) {
        return;
      }

      const userFullName = `${user.givenName} ${user.surname || ''}`.trim();
      const personProps = this.buildPosthogPersonProperties(user, userFullName);
      if (this.identifiedPosthogUserId !== distinctId) {
        this.identifiedPosthogUserId = distinctId;
      }

      posthog.identify(distinctId, personProps);

      this.tryCapturePosthogLogin();
    });
  }

  private buildPosthogPersonProperties(
    user: ProfileType,
    userFullName: string,
  ): Record<string, string> {
    const props: Record<string, string> = {
      name: user.displayName || userFullName,
    };
    if (user.mail) {
      props['email'] = user.mail;
    }
    if (user.userPrincipalName) {
      props['email'] = props['email'] || user.userPrincipalName;
      props['userPrincipalName'] = user.userPrincipalName;
    }
    return props;
  }

  private tryCapturePosthogLogin(): void {
    if (!environment.POSTHOG_ENABLED_TRACKING || !this.pendingPosthogLoginCapture) {
      return;
    }

    const user = this.authService.UserObj().user as ProfileType | undefined;
    if (!user?.givenName || !user.id) {
      return;
    }

    this.pendingPosthogLoginCapture = false;
    const userFullName = `${user.givenName} ${user.surname || ''}`.trim();

    this.apiService.getInferenceAbout().subscribe({
      next: (response) => {
        this.capturePosthogLogin(userFullName, response?.data?.modelDetails?.version ?? null);
      },
      error: () => {
        this.capturePosthogLogin(userFullName, null);
      },
    });
  }

  private capturePosthogLogin(
    userFullName: string,
    modelVersion: number | string | null,
  ): void {
    const user = this.authService.UserObj().user as ProfileType | undefined;
    const personProps = user
      ? this.buildPosthogPersonProperties(user, userFullName)
      : { name: userFullName };

    posthog.capture('User Logged In', {
      $set: personProps,
      name: userFullName,
      environment: environment.navType,
      modelVersion,
    });
  }

  checkAndSetActiveAccount(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }

    this.syncEditorRoleFromActiveAccount();
  }

  private syncEditorRoleFromActiveAccount(): void {
    const account = this.msalService.instance.getActiveAccount();
    const roles = account?.idTokenClaims?.['roles'];
    console.log('roles',roles);
    const isEditor = Array.isArray(roles)
 
      ? (roles.includes('Editors') || roles.includes('Editor'))
      : roles === 'Editors' || roles === 'Editor';
      console.log('isEditor',isEditor);
    this.authService.setIsEditor(isEditor);
  }

  ngOnInit(): void {
    this.broadcastService.msalSubject$
      .pipe(
        filter((message) => message.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.pendingPosthogLoginCapture = true;
        this.tryCapturePosthogLogin();
      });

    this.broadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.syncEditorRoleFromActiveAccount();
        this.getProfile();
      });
  }

  setLoginDisplay(): void {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  getProfile(): void {
    const graphUrl = environment.AAD_GRAPH_ENDPOINT ?? 'https://graph.microsoft.com/v1.0/me';
    this.http.get<ProfileType>(graphUrl).subscribe((profile: ProfileType) => {
      this.authService.setUserAuthData({ user: profile });
    });
  }
}
